// MorrowOS file VAULT — folders grid on top, detailed file list below.
import { useUser } from "@clerk/react";
import {
  LayoutGridIcon,
  ListIcon,
  PlusIcon,
  RefreshCwIcon,
  SparklesIcon,
  UploadIcon,
  VaultIcon,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from "react";
import { useSearch } from "@tanstack/react-router";

import { useI18n } from "~/i18n";
import { Button } from "../ui/button";
import { Spinner } from "../ui/spinner";
import { toastManager } from "../ui/toast";
import { Toggle, ToggleGroup } from "../ui/toggle-group";
import { MorrowWorkspacePage } from "../workspace/MorrowWorkspacePage";
import {
  createVaultFolder,
  deleteVaultFolder as deleteVaultFolderApi,
  deleteVaultItem as deleteVaultItemApi,
  downloadVaultItem,
  fetchVaultFolders,
  fetchVaultItems,
  moveVaultItemToFolder,
  reindexVault,
  renameVaultFolder as renameVaultFolderApi,
  uploadToVault,
  type VaultFolder,
  type VaultItem,
} from "../../morrowVault";
import { migrateLocalVaultFolders } from "./vaultFolderMigration";
import { MorrowVaultBrowseSkeleton } from "../morrow/MorrowPageSkeletons";
import { VaultCreateFolderDialog } from "./VaultCreateFolderDialog";
import { VaultDeleteFolderDialog } from "./VaultDeleteFolderDialog";
import { VaultFileGrid } from "./VaultFileGrid";
import { VaultFileList } from "./VaultFileList";
import { VaultFolderCard } from "./VaultFolderCard";
import { VaultPreviewDialog } from "./VaultPreviewDialog";
import {
  browseVaultFolders,
  customFolderRawId,
  customVaultFolderId,
  folderDisplayLabel,
  groupVaultItemsByFolder,
  type VaultFolderId,
} from "./vaultFolders";
import {
  createCustomVaultFolder,
  customVaultFoldersStorageKey,
  loadCustomVaultFolders,
  loadVaultItemAssignments,
  removeVaultItemAssignment,
  saveCustomVaultFolders,
  saveVaultItemAssignments,
  setVaultItemFolderAssignment,
  vaultItemAssignmentsStorageKey,
} from "./vaultCustomFolders";

type VaultFileView = "list" | "grid";

const VAULT_FILE_VIEW_KEY = "morrow-vault-file-view";

function readStoredFileView(): VaultFileView {
  if (typeof window === "undefined") return "grid";
  const stored = window.localStorage.getItem(VAULT_FILE_VIEW_KEY);
  return stored === "list" ? "list" : "grid";
}

type PendingUpload = {
  readonly id: string;
  readonly name: string;
  readonly mimeType: string;
};

let pendingUploadCounter = 0;

function nextPendingUploadId(): string {
  pendingUploadCounter += 1;
  return `pending-${pendingUploadCounter}`;
}

export function MorrowVaultPage() {
  const { t } = useI18n();
  const { item: itemFromSearch } = useSearch({ strict: false });
  const { user } = useUser();
  const [items, setItems] = useState<VaultItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [reindexing, setReindexing] = useState(false);
  const [pendingUploads, setPendingUploads] = useState<PendingUpload[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<VaultFolderId>("all");
  const [deletingIds, setDeletingIds] = useState<ReadonlySet<string>>(() => new Set());
  const [previewItem, setPreviewItem] = useState<VaultItem | null>(null);
  const [fileView, setFileView] = useState<VaultFileView>(() => readStoredFileView());
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [folderPendingDelete, setFolderPendingDelete] = useState<{
    readonly id: string;
    readonly name: string;
    readonly count: number;
  } | null>(null);
  const [folderPendingRename, setFolderPendingRename] = useState<{
    readonly id: string;
    readonly name: string;
  } | null>(null);
  const [serverFolders, setServerFolders] = useState<VaultFolder[]>(() => []);
  const deletingIdsRef = useRef<ReadonlySet<string>>(new Set());
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const dragDepthRef = useRef(0);

  const addedByLabel =
    user?.fullName?.trim() ||
    [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim() ||
    t("vault.addedByYou");

  const customFoldersStorageKey = customVaultFoldersStorageKey(user?.id);
  const itemAssignmentsStorageKey = vaultItemAssignmentsStorageKey(user?.id);

  const loadFolders = useCallback(async () => {
    setServerFolders(await fetchVaultFolders());
  }, []);

  // Folders are server rows now. Anyone who organised their vault before that has the work in
  // localStorage, invisible to the server, so carry it up ONCE before the first read — the lift
  // keeps local state on any failure, so this is safe to attempt on every mount.
  useEffect(() => {
    let alive = true;
    void (async () => {
      await migrateLocalVaultFolders(user?.id);
      if (alive) await loadFolders();
    })();
    return () => {
      alive = false;
    };
  }, [user?.id, loadFolders]);

  const load = useCallback(async () => {
    setLoading(true);
    const next = await fetchVaultItems();
    setItems(next);
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Make already-uploaded files searchable + item-indexed (extraction otherwise runs
  // only on new uploads). Runs async server-side; results appear as each file finishes.
  const handleReindex = useCallback(async () => {
    setReindexing(true);
    const queued = await reindexVault();
    setReindexing(false);
    if (queued < 0) {
      toastManager.add({ type: "error", title: t("vault.reindexFailed") });
      return;
    }
    toastManager.add({ title: t("vault.reindexStarted", { count: queued }) });
  }, [t]);

  // The server folders, in the shape the browse UI already understands.
  const customFolders = useMemo(
    () => serverFolders.map((f) => ({ id: f.id, name: f.name, createdAt: f.createdAt })),
    [serverFolders],
  );
  // Each item now carries its own folderId, so the assignment map is derived from the items
  // rather than kept beside them — one source of truth instead of two that can disagree.
  const itemFolderAssignments = useMemo(() => {
    const next: Record<string, string> = {};
    for (const item of items) if (item.folderId) next[item.id] = item.folderId;
    return next;
  }, [items]);
  const groups = useMemo(
    () => groupVaultItemsByFolder(items, customFolders, itemFolderAssignments),
    [items, customFolders, itemFolderAssignments],
  );
  const folders = useMemo(() => browseVaultFolders(customFolders), [customFolders]);
  const listItems = groups[selectedFolder] ?? [];

  const createFolder = useCallback(
    (name: string) => {
      void (async () => {
        const folder = await createVaultFolder(name);
        if (!folder) {
          toastManager.add({ type: "error", title: t("vault.folderCreateFailed") });
          return;
        }
        setServerFolders((current) => [...current, folder]);
        setSelectedFolder(customVaultFolderId(folder.id));
      })();
    },
    [t],
  );

  /** File an item, or unfile it with null. Optimistic, then reconciled from the server. */
  const assignItemToFolder = useCallback(
    (itemId: string, folderId: string | null) => {
      // Dropping an item onto the folder it already lives in is a no-op — no request, no churn.
      const current = items.find((it) => it.id === itemId);
      if (current && (current.folderId ?? null) === folderId) return;
      setItems((prev) => prev.map((it) => (it.id === itemId ? { ...it, folderId } : it)));
      void (async () => {
        const ok = await moveVaultItemToFolder(itemId, folderId);
        if (!ok) {
          toastManager.add({ type: "error", title: t("vault.moveFailed") });
          await load();
        }
        await loadFolders();
      })();
    },
    [items, load, loadFolders, t],
  );

  /** Delete a folder. `deleteContents` also deletes the files inside; otherwise they're unfiled. */
  const deleteFolder = useCallback(
    (id: string, deleteContents: boolean) => {
      // Drop it from the grid immediately; if the delete fails we reload and it returns.
      setServerFolders((current) => current.filter((f) => f.id !== id));
      if (deleteContents) {
        setItems((current) => current.filter((it) => it.folderId !== id));
      } else {
        setItems((current) =>
          current.map((it) => (it.folderId === id ? { ...it, folderId: null } : it)),
        );
      }
      if (selectedFolder === customVaultFolderId(id)) setSelectedFolder("all");
      void (async () => {
        const ok = await deleteVaultFolderApi(id, { deleteContents });
        if (!ok) toastManager.add({ type: "error", title: t("vault.folderDeleteFailed") });
        await loadFolders();
        await load();
      })();
    },
    [load, loadFolders, selectedFolder, t],
  );

  /** Rename a folder. Optimistic, reconciled from the server. */
  const renameFolder = useCallback(
    (id: string, name: string) => {
      setServerFolders((current) => current.map((f) => (f.id === id ? { ...f, name } : f)));
      void (async () => {
        const ok = await renameVaultFolderApi(id, name);
        if (!ok) toastManager.add({ type: "error", title: t("vault.folderRenameFailed") });
        await loadFolders();
      })();
    },
    [loadFolders, t],
  );

  const setFileViewMode = useCallback((next: VaultFileView) => {
    setFileView(next);
    window.localStorage.setItem(VAULT_FILE_VIEW_KEY, next);
  }, []);

  useEffect(() => {
    if (!itemFromSearch || items.length === 0) return;
    const item = items.find((entry) => entry.id === itemFromSearch);
    if (item) setPreviewItem(item);
  }, [itemFromSearch, items]);

  const openItem = useCallback((item: VaultItem) => {
    setPreviewItem(item);
  }, []);

  const remove = useCallback(
    async (item: VaultItem) => {
      if (deletingIdsRef.current.has(item.id)) return;

      const nextDeleting = new Set(deletingIdsRef.current).add(item.id);
      deletingIdsRef.current = nextDeleting;
      setDeletingIds(nextDeleting);

      const ok = await deleteVaultItemApi(item.id);

      const doneDeleting = new Set(deletingIdsRef.current);
      doneDeleting.delete(item.id);
      deletingIdsRef.current = doneDeleting;
      setDeletingIds(doneDeleting);

      if (ok) {
        setItems((current) => current.filter((entry) => entry.id !== item.id));
        // The item's folder went with its row, so there is no second map to clean up — but the
        // folder's item count changed.
        void loadFolders();
        return;
      }

      toastManager.add({
        type: "error",
        title: t("vault.deleteFailed"),
        description: item.name,
      });
    },
    [itemAssignmentsStorageKey, t],
  );

  const openPicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  /** The folder an upload should land in: whichever custom folder is being viewed, else unfiled. */
  const uploadTargetFolderId = useMemo(
    () => customFolderRawId(selectedFolder) ?? null,
    [selectedFolder],
  );

  const handleFiles = useCallback(
    async (files: FileList | null, intoFolderId?: string | null) => {
      if (!files || files.length === 0) return;
      // An explicit destination (a drop onto a folder card) wins over the folder being viewed.
      const targetFolderId = intoFolderId !== undefined ? intoFolderId : uploadTargetFolderId;
      const fileArray = Array.from(files);
      const placeholders: PendingUpload[] = fileArray.map((file) => ({
        id: nextPendingUploadId(),
        name: file.name,
        mimeType: file.type || "application/octet-stream",
      }));
      setPendingUploads((current) => [...placeholders, ...current]);
      setUploading(true);
      try {
        const uploaded: VaultItem[] = [];
        for (let index = 0; index < fileArray.length; index += 1) {
          const file = fileArray[index]!;
          const placeholderId = placeholders[index]!.id;
          // The destination travels WITH the upload, so a dropped file cannot end up stored
          // but unfiled if a follow-up request fails.
          const item = await uploadToVault(file, {
            ...(targetFolderId ? { folderId: targetFolderId } : {}),
          });
          if (item) uploaded.push(item);
          setPendingUploads((current) => current.filter((entry) => entry.id !== placeholderId));
        }
        if (uploaded.length > 0) {
          setItems((current) => {
            const seen = new Set(uploaded.map((entry) => entry.id));
            return [...uploaded, ...current.filter((entry) => !seen.has(entry.id))];
          });
          void loadFolders();
        }
        if (uploaded.length !== fileArray.length) void load();
      } finally {
        setUploading(false);
        setPendingUploads([]);
      }
    },
    [load, loadFolders, uploadTargetFolderId],
  );

  const onDragEnter = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current += 1;
    if (event.dataTransfer.types.includes("Files")) {
      setDragActive(true);
    }
  }, []);

  const onDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = "copy";
  }, []);

  const onDragLeave = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) {
      setDragActive(false);
    }
  }, []);

  const onDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      dragDepthRef.current = 0;
      setDragActive(false);
      void handleFiles(event.dataTransfer.files);
    },
    [handleFiles],
  );

  const showEmpty =
    !loading && items.length === 0 && pendingUploads.length === 0 && customFolders.length === 0;
  const showContent = !loading && !showEmpty;

  return (
    <MorrowWorkspacePage
      title={t("vault.title")}
      subtitle={t("vault.subtitle")}
      icon={<VaultIcon />}
      wide
      contentClassName="flex min-h-0 flex-1 flex-col gap-0 p-0 sm:p-0"
      action={
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={openPicker}
            disabled={uploading}
          >
            {uploading ? <Spinner /> : <UploadIcon aria-hidden="true" />}
            {uploading ? t("vault.uploading") : t("vault.upload")}
          </Button>
          <Button type="button" size="sm" onClick={() => setCreateFolderOpen(true)}>
            <PlusIcon aria-hidden="true" />
            {t("vault.createFolderTitle")}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void handleReindex()}
            disabled={reindexing || loading || uploading || items.length === 0}
            title={t("vault.reindexHint")}
          >
            {reindexing ? <Spinner /> : <SparklesIcon aria-hidden="true" />}
            {reindexing ? t("vault.reindexing") : t("vault.reindex")}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void load()}
            disabled={loading || uploading}
            title={t("common.refresh")}
          >
            <RefreshCwIcon
              className={loading ? "size-4 animate-spin" : "size-4"}
              aria-hidden="true"
            />
            {t("common.refresh")}
          </Button>
        </div>
      }
    >
      <div
        className="relative flex min-h-0 flex-1 flex-col gap-8 overflow-hidden px-4 py-6 sm:px-6"
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        {dragActive ? (
          <div
            className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-2xl border-2 border-dashed border-primary/60 bg-primary/5 backdrop-blur-[1px]"
            aria-hidden
          >
            <p className="text-sm font-medium text-primary">{t("vault.dropHint")}</p>
          </div>
        ) : null}

        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="sr-only"
          onChange={(event) => {
            void handleFiles(event.target.files);
            event.target.value = "";
          }}
        />

        {uploading ? (
          <div
            className="h-1 shrink-0 overflow-hidden rounded-full bg-muted"
            role="progressbar"
            aria-label={t("vault.uploading")}
          >
            <div className="h-full w-1/3 animate-pulse rounded-full bg-primary" />
          </div>
        ) : null}

        {loading ? (
          <MorrowVaultBrowseSkeleton />
        ) : showEmpty ? (
          <EmptyState
            title={t("vault.empty")}
            hint={t("vault.emptyHint")}
            dropHint={t("vault.dropHint")}
            uploadLabel={uploading ? t("vault.uploading") : t("vault.upload")}
            uploading={uploading}
            onUpload={openPicker}
          />
        ) : showContent ? (
          <div className="flex min-h-0 flex-1 flex-col gap-8">
            <section className="shrink-0 space-y-4">
              <h2 className="text-lg font-semibold tracking-tight text-foreground">
                {t("vault.folders")}
              </h2>
              <div className="-mx-1 overflow-x-auto px-1 py-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                <div className="flex w-max flex-nowrap items-start gap-3">
                  {folders.map((folder) => {
                    const previewItems = groups[folder.id];
                    const rawId = customFolderRawId(folder.id);
                    const count = previewItems?.length ?? 0;
                    const label = folderDisplayLabel(folder, t);
                    return (
                      <VaultFolderCard
                        key={folder.id}
                        folder={folder}
                        label={label}
                        count={count}
                        {...(previewItems ? { previewItems } : {})}
                        selected={selectedFolder === folder.id}
                        onSelect={() => setSelectedFolder(folder.id)}
                        {...(rawId
                          ? {
                              // Drop files to upload INTO this folder, or drop a file already in
                              // the vault to move it here. Only real folders accept a drop — the
                              // mime buckets are derived, so "move into Images" is meaningless.
                              onDropFiles: (files: FileList) => {
                                void handleFiles(files, rawId);
                              },
                              onDropItem: (itemId: string) => {
                                assignItemToFolder(itemId, rawId);
                              },
                              onRename: () => setFolderPendingRename({ id: rawId, name: label }),
                              onDelete: () =>
                                setFolderPendingDelete({ id: rawId, name: label, count }),
                            }
                          : folder.id === "all"
                            ? {
                                // Drag a file onto "All" to pull it OUT of its folder — the
                                // drag-OUT gesture, moving it back to unfiled (root).
                                onDropItem: (itemId: string) => {
                                  assignItemToFolder(itemId, null);
                                },
                              }
                            : {})}
                      />
                    );
                  })}
                </div>
              </div>
            </section>

            <section className="flex min-h-0 flex-1 flex-col gap-4">
              <div className="flex shrink-0 items-center justify-between gap-3">
                <h2 className="text-lg font-semibold tracking-tight text-foreground">
                  {t("vault.files")}
                </h2>
                <div className="flex items-center gap-2">
                  {pendingUploads.length > 0 ? (
                    <span className="text-xs text-muted-foreground">
                      {t("vault.uploadingFile")}
                    </span>
                  ) : null}
                  <ToggleGroup
                    className="shrink-0 gap-0.5"
                    size="sm"
                    value={[fileView]}
                    onValueChange={(value) => {
                      const next = value[0];
                      if (next === "list" || next === "grid") {
                        setFileViewMode(next);
                      }
                    }}
                  >
                    <Toggle aria-label={t("vault.viewList")} value="list" variant="ghost">
                      <ListIcon className="size-3.5" aria-hidden="true" />
                    </Toggle>
                    <Toggle aria-label={t("vault.viewGrid")} value="grid" variant="ghost">
                      <LayoutGridIcon className="size-3.5" aria-hidden="true" />
                    </Toggle>
                  </ToggleGroup>
                </div>
              </div>
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                {fileView === "grid" ? (
                  <div className="min-h-0 flex-1 overflow-y-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                    <VaultFileGrid
                      items={listItems}
                      addedByLabel={addedByLabel}
                      customFolders={customFolders}
                      itemFolderAssignments={itemFolderAssignments}
                      deletingIds={deletingIds}
                      onOpen={openItem}
                      onDownload={(item) => void downloadVaultItem(item)}
                      onDelete={(item) => void remove(item)}
                      onAssignFolder={assignItemToFolder}
                    />
                  </div>
                ) : (
                  <VaultFileList
                    items={listItems}
                    addedByLabel={addedByLabel}
                    customFolders={customFolders}
                    itemFolderAssignments={itemFolderAssignments}
                    deletingIds={deletingIds}
                    onOpen={openItem}
                    onDownload={(item) => void downloadVaultItem(item)}
                    onDelete={(item) => void remove(item)}
                    onAssignFolder={assignItemToFolder}
                  />
                )}
              </div>
            </section>
          </div>
        ) : null}
      </div>

      <VaultPreviewDialog
        item={previewItem}
        open={previewItem !== null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setPreviewItem(null);
        }}
      />

      <VaultCreateFolderDialog
        open={createFolderOpen}
        onOpenChange={setCreateFolderOpen}
        onCreate={createFolder}
      />

      {/* Keyed by folder id so the input always seeds with the right current name. */}
      <VaultCreateFolderDialog
        key={folderPendingRename?.id ?? "rename"}
        open={folderPendingRename !== null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setFolderPendingRename(null);
        }}
        initialName={folderPendingRename?.name ?? ""}
        onCreate={(name) => {
          if (folderPendingRename) renameFolder(folderPendingRename.id, name);
        }}
      />

      <VaultDeleteFolderDialog
        open={folderPendingDelete !== null}
        folderName={folderPendingDelete?.name ?? ""}
        fileCount={folderPendingDelete?.count ?? 0}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setFolderPendingDelete(null);
        }}
        onConfirm={(deleteContents) => {
          if (folderPendingDelete) deleteFolder(folderPendingDelete.id, deleteContents);
        }}
      />
    </MorrowWorkspacePage>
  );
}

function EmptyState({
  title,
  hint,
  dropHint,
  uploadLabel,
  uploading,
  onUpload,
}: {
  title: string;
  hint: string;
  dropHint: string;
  uploadLabel: string;
  uploading: boolean;
  onUpload: () => void;
}) {
  return (
    <section className="flex min-h-full flex-1 flex-col items-center justify-center overflow-hidden rounded-2xl border border-dashed border-border/70 bg-muted/10 px-6 py-16 text-center">
      <div className="relative mb-5 flex size-16 items-center justify-center">
        <span className="absolute inset-0 rounded-2xl bg-primary/10" aria-hidden />
        <span className="absolute inset-2 rounded-xl bg-primary/10" aria-hidden />
        <VaultIcon className="relative size-7 text-primary" />
      </div>
      <h2 className="text-base font-semibold tracking-tight text-foreground">{title}</h2>
      <p className="mt-2 max-w-md text-sm leading-6 text-muted-foreground">{hint}</p>
      <p className="mt-1 max-w-md text-xs text-muted-foreground/80">{dropHint}</p>
      <Button type="button" size="sm" className="mt-5" onClick={onUpload} disabled={uploading}>
        {uploading ? <Spinner /> : <UploadIcon aria-hidden="true" />}
        {uploadLabel}
      </Button>
    </section>
  );
}
